import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useEffect, useCallback } from "react";

import { Stack } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { Card, Avatar, Tooltip, TextField, IconButton, Typography, CardContent, Autocomplete, ListItemText } from "@mui/material";

import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hooks";

import { useBoolean } from "src/hooks/use-boolean";
import { useAuthContext } from "src/hooks/auth/use-auth-context";

import { useTranslate } from "src/locales";
import { IOrgCandidate, LinkedOrgCandidate } from "src/services/candidates/candidates.types";
import { useGetOrganizationCandidatesQuery } from "src/services/candidates/candidates.service";
import { useGetContactByIdQuery, useUpdateContactCandidateLinkMutation } from "src/services/contacts/contacts.service";

import Iconify from "src/components/iconify";
import { ConfirmDialog } from "src/components/custom-dialog";
import TruncatedTextField from "src/components/truncate-text/truncated-text-field";

type Props = {
    contactId: string;
}

const DEFAULT_PAGE = {
    page: 1,
    per_page: 10,
};

const CandidateLinkCard = ({ contactId }: Props) => {

    const showEditUserDialog = useBoolean();

    const { t } = useTranslate();

    const authContext = useAuthContext();

    const router = useRouter();

    const { data: contact } = useGetContactByIdQuery(contactId, {
        skip: !contactId,
    });

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedCandidate, setSelectedCandidate] = useState<LinkedOrgCandidate | IOrgCandidate | null>(null);
    const [currentCandidate, setCurrentCandidate] = useState<LinkedOrgCandidate | IOrgCandidate | null>(null);

    const { currentData: candidates, isLoading: isCandidatesLoading } = useGetOrganizationCandidatesQuery(
        {
            search_query: searchQuery,
            page: DEFAULT_PAGE,
        },
        { skip: !authContext.organization?.id }
    );

    const [updateCandidateLink, { isLoading: isUpdatingCandidateLink }] = useUpdateContactCandidateLinkMutation();

    const onSearchQueryChange = useMemo(
        () => debounce((value: string) => {
            setSearchQuery(value);
        }, 500),
        [setSearchQuery]
    );

    useEffect(() => {
        if (contact?.linked_candidate) {
            setSelectedCandidate(contact?.linked_candidate);
            setCurrentCandidate(contact?.linked_candidate);
        }
        else {
            setSelectedCandidate(null);
            setCurrentCandidate(null);
        }
    }, [contact?.linked_candidate]);

    const handleUpdateLink = useCallback(async () => {

        if (!contact || !selectedCandidate) return;

        try {
            await updateCandidateLink({
                contact_id: contactId,
                data: {
                    candidate_id: selectedCandidate.id,
                }
            }).unwrap();

            enqueueSnackbar(t('contacts.api.update_candidate_link.success'), { variant: 'success' });
            showEditUserDialog.onFalse();
        } catch (error) {
            enqueueSnackbar(t('contacts.api.update_candidate_link.default_error'), { variant: 'error' });
        }
    }, [contact, selectedCandidate, showEditUserDialog, contactId, updateCandidateLink, t]);

    const handleRemoveLink = useCallback(async () => {
        if (!contact) return;

        try {
            await updateCandidateLink({
                contact_id: contactId,
                data: {
                    candidate_id: null,
                }
            }).unwrap();

            enqueueSnackbar(t('contacts.api.remove_candidate_link.success'), { variant: 'success' });
            showEditUserDialog.onFalse();
        } catch (error) {
            enqueueSnackbar(t('contacts.api.remove_candidate_link.default_error'), { variant: 'error' });
        }
    }, [contact, showEditUserDialog, contactId, updateCandidateLink, t]);

    return (
        <>
            <Card>
                <CardContent>
                    <Stack spacing={3}>
                        <Stack direction="row" spacing={2} display="flex" alignItems="center">
                            <Avatar
                                alt={currentCandidate ? currentCandidate.candidate_profile?.first_name : undefined}
                                src={currentCandidate ? currentCandidate.candidate_profile?.first_name : undefined}
                                variant="rounded"
                                sx={{ width: 48, height: 48 }}
                            />

                            <Stack direction="row" display="flex" width="100%" justifyContent="space-between">
                                <ListItemText
                                    primary={t('contacts.detail.candidate_link.title')}
                                    secondary={
                                        <TruncatedTextField
                                            text={currentCandidate ? `${currentCandidate.candidate_profile.first_name} ${currentCandidate.candidate_profile.last_name}` : t('contacts.detail.account_owner.no_account_owner')}
                                            limit={15}
                                            mode="characters"
                                            showMoreEnabled={false}
                                            variant="body2"
                                            onClick={() => {
                                                if (currentCandidate) {
                                                    router.push(paths.dashboard.candidates.detail(currentCandidate?.id as string));
                                                }
                                            }}
                                            sx={{ '&:hover': currentCandidate ? { cursor: 'pointer', textDecoration: 'underline' } : {} }}
                                        />
                                    }
                                />

                                <Stack direction="row" spacing={1} display="flex" alignItems="center">

                                    {
                                        contact?.linked_candidate && (
                                            <Tooltip title={t('contacts.detail.candidate_link.remove_tooltip')} arrow placement="top">
                                                <IconButton onClick={handleRemoveLink} color="error" sx={{ p: .3 }}>
                                                    <Iconify icon="mingcute:user-remove-fill" />
                                                </IconButton>
                                            </Tooltip>
                                        )
                                    }

                                    <Tooltip title={t('contacts.detail.candidate_link.update_tooltip')} arrow placement="top">
                                        <IconButton onClick={showEditUserDialog.onTrue} sx={{ p: .3 }}>
                                            <Iconify icon="solar:pen-bold" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {
                (contact) && (
                    <ConfirmDialog
                        open={showEditUserDialog.value}
                        title={t('contacts.detail.candidate_link.edit')}
                        onClose={showEditUserDialog.onFalse}
                        content={
                            <Autocomplete
                                sx={{
                                    paddingTop: 2,
                                }}
                                multiple={false}
                                options={candidates?.results as LinkedOrgCandidate[] ?? []}
                                value={selectedCandidate as LinkedOrgCandidate}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t(
                                            'contacts.detail.candidate_link.candidate_autocomplete.label'
                                        )}
                                        placeholder={t(
                                            'contacts.detail.candidate_link.candidate_autocomplete.placeholder'
                                        )}
                                    />
                                )}
                                onInputChange={(_e, value) => onSearchQueryChange(value)}
                                onChange={(_e, value: LinkedOrgCandidate | null) => setSelectedCandidate(value)}
                                renderOption={({ key, ...rest }, option: LinkedOrgCandidate) => (
                                    <li key={key} {...rest}>
                                        <Stack direction="column">
                                            <Typography variant="body1">
                                                {`${option.candidate_profile.first_name} ${option.candidate_profile.last_name}`}
                                            </Typography>
                                        </Stack>
                                    </li>
                                )}
                                getOptionLabel={(option) => `${option.candidate_profile.first_name} ${option.candidate_profile.last_name}`}
                                isOptionEqualToValue={(option, value) =>
                                    option && (option as LinkedOrgCandidate).id === (value as LinkedOrgCandidate).id
                                }
                                getOptionKey={(option) => (option as LinkedOrgCandidate).id as string}
                            />
                        }
                        action={
                            <LoadingButton
                                variant="contained"
                                disabled={!selectedCandidate || isUpdatingCandidateLink}
                                onClick={handleUpdateLink}
                                loading={isUpdatingCandidateLink}>
                                {t('contacts.detail.candidate_link.assign')}
                            </LoadingButton>
                        }
                    />
                )
            }

        </>
    )

}

export default CandidateLinkCard; 